import { getAspectRatioStyle } from "@common/controls/AspectRatioContainer/lib";
import Script from "@common/controls/Html/Script";
import { uuid } from "@common/lib/misc";
import storage from "@common/lib/storage";
import { parseQueryString } from "@common/lib/url";
import classnames from "classnames";
import { isUndefined } from "lodash";
import * as React from "react";
import { type VideoProps } from "../component";
import { DEFAULT_STORAGE_INFO, getAllow, type StorageInfo } from "../lib";
import { getSrc, getVideoId } from "./lib";

export type YouTubeVideoProps = VideoProps & {
  videoId?: string;
};

const YouTubeVideoWithApi = (props: YouTubeVideoProps) => {
  const id = React.useMemo(() => `ytp_${uuid()}`, []);
  const videoId = React.useMemo(() => props.videoId ?? getVideoId(props.url), [props.videoId, props.url]);
  const options = React.useMemo(() => {
    const qs = parseQueryString(props.url);
    return props.isBackground
      ? {
          ...qs,
          autohide: 1,
          autoplay: 1,
          controls: 0,
          loop: 1,
          mute: 1,
          showinfo: 0,
          playlist: videoId
        }
      : {
          ...qs,
          autoplay: props.autoPlay ? 1 : 0,
          controls: props.showControls || isUndefined(props.showControls) ? 1 : 0,
          loop: props.loop ? 1 : 0,
          mute: props.autoPlay ? 1 : props.mute ? 1 : 0,
          playlist: props.loop ? videoId : qs.playlist
        };
  }, [
    props.url,
    props.isBackground,
    props.autoPlay,
    props.showControls,
    props.loop,
    props.autoPlay,
    props.mute,
    props.loop
  ]);

  const [isReady, setIsReady] = React.useState(false);

  const player = React.useRef<any>(null);

  const onScriptLoaded = React.useCallback(() => {
    window["YT"].ready(() => {
      setIsReady(true);
    });
  }, []);

  const storageKey = React.useMemo(() => `youtube_${videoId}`.toLowerCase(), [videoId]);

  React.useEffect(() => {
    if (!isReady) return;

    let timeout;
    const onPause = () => storage.patch<StorageInfo>(storageKey, { isPaused: true }, DEFAULT_STORAGE_INFO);
    const onPlay = () => storage.patch<StorageInfo>(storageKey, { isPaused: false }, DEFAULT_STORAGE_INFO);
    const updateTime = (player: any) => {
      const seconds = player.getCurrentTime();
      storage.patch<StorageInfo>(storageKey, { seconds }, DEFAULT_STORAGE_INFO);
      timeout = setTimeout(() => updateTime(player), 1000);
    };
    const onReady = () => {
      const p = player.current!;
      const si = storage.get<StorageInfo>(storageKey);
      if (si?.seconds) {
        p.seekTo(si.seconds, true);
        if (si.isPaused) {
          p.pauseVideo();
        } else {
          p.playVideo();
        }
      }
      timeout = setTimeout(() => {
        updateTime(p);
      });
    };

    player.current && player.current.destroy();
    player.current = new window["YT"].Player(id, {
      videoId,
      playerVars: options,
      events: {
        onReady,
        onStateChange: (e) => {
          switch (e.data) {
            case window["YT"].PlayerState.PLAYING:
              onPlay();
              props.onStartPlay && props.onStartPlay();
              break;
            case window["YT"].PlayerState.PAUSED:
              onPause();
              break;
            default:
              break;
          }
        }
      }
    });

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [isReady, id, options, videoId]); // TODO: handle changes in props.onStartPlay

  React.useEffect(
    () => () => {
      player.current && player.current.destroy();
      player.current = null;
    },
    []
  );

  return (
    <div
      className={classnames("video_container", { background: props.isBackground })}
      style={getAspectRatioStyle(props.aspectRatio)}
    >
      <div className="video youtube_video">
        <Script src="https://www.youtube.com/iframe_api" isAsync onLoaded={onScriptLoaded} />

        <div id={id} style={{ width: "100%", height: "100%" }}></div>

        {props.isBackground && <div className="overlay" />}
      </div>
    </div>
  );
};

const YouTubeVideoApiless = (props: YouTubeVideoProps) => (
  <div
    className={classnames("video_container", { background: props.isBackground })}
    style={getAspectRatioStyle(props.aspectRatio)}
  >
    <div className="video youtube_video">
      <iframe allow={getAllow(props)} height="100%" width="100%" src={getSrc(props)} />

      {props.isBackground && <div className="overlay" />}
    </div>
  </div>
);

const YouTubeVideo = (props: YouTubeVideoProps) =>
  props.onStartPlay ? <YouTubeVideoWithApi {...props} /> : <YouTubeVideoApiless {...props} />;
export default YouTubeVideo;
